import {
  computed,
  type ComputedRef,
  type MaybeRefOrGetter,
  toValue,
} from 'vue';

import useAuth from '~/store/auth/useAuth';

/** Retorno quando o argumento é um objeto de roles por nome */
// eslint-disable-next-line no-unused-vars -- Key é variável do mapped type
type PermissionMap<T> = { [Key in keyof T]: ComputedRef<boolean> };

/**
 * Composable de permissão: use em componentes para v-if, :disabled ou lógica no script.
 * Verifica roles em allowedRoles (fintech-backoffice + backoffice) via useAuth().
 *
 * Uso único: usePermission(['role']) → { hasAccess }
 * Várias: usePermission({ delete: [...], publish: [...], view: [...], write: [...] })
 */
export default function usePermission<
  T extends Record<string, string[]> | string[],
>(
  rolesOrMap: MaybeRefOrGetter<T>,
): T extends string[] ? { hasAccess: ComputedRef<boolean> } : PermissionMap<T> {
  const { data: auth } = useAuth();
  const value = computed(() => toValue(rolesOrMap));

  const hasAccess = computed(() => {
    const resolvedValue = value.value;
    if (Array.isArray(resolvedValue)) {
      return auth.value?.hasSomeRole?.(resolvedValue) ?? false;
    }
    return false;
  });

  const resolvedValue = value.value;
  if (Array.isArray(resolvedValue)) {
    return { hasAccess } as T extends string[]
      ? { hasAccess: ComputedRef<boolean> }
      : PermissionMap<T>;
  }

  const map = resolvedValue as Record<string, string[]>;
  return Object.fromEntries(
    Object.keys(map).map((key) => [
      key,
      computed(() => auth.value?.hasSomeRole?.(map[key]) ?? false),
    ]),
  ) as T extends string[] ? { hasAccess: ComputedRef<boolean> } : PermissionMap<T>;
}
